<template>
  <mobile-responsive-shell :title="title">
    <div v-if="sale && sale.reference" class="sal-detail">
      <div class="sal-detail-status text-center mb-2" :class="sale.validated ? 'text-success' : 'text-warning'">
        <span v-if="sale.validated"><i class="bi bi-check2"></i></span>
        BL <strong>{{ sale.reference }}</strong>
        <span class="text-muted"> · {{ sale.date }}</span>
      </div>

      <div class="sal-detail-actions">
        <template
          v-if="sale.reference && ($userRole == 'Admin' || $userRole == 'Commercial') && sale.totalPriceTTC == sale.amountUnpaid"
        >
          <button type="button" class="btn btn-sm btn-outline-secondary" @click="recalculationPrices(sale.reference)">
            <i class="bi bi-coin"></i> Recalculer
          </button>
          <button type="button" class="btn btn-sm btn-outline-secondary" @click="addManutention(sale.reference)">
            <i class="bi bi-plus"></i> Manutention
          </button>
        </template>
        <button
          v-if="sale.order_reference && sale.reference && ($userRole == 'Admin' || $userRole == 'Commercial') && sale.totalPriceTTC == sale.amountUnpaid"
          type="button"
          class="btn btn-sm btn-outline-secondary"
          @click="$router.push({ name: 'orders-details', params: { reference: sale.order_reference } })"
        >
          <i class="bi bi-cart3"></i> Commande
        </button>
        <button
          v-if="!sale.validated"
          type="button"
          class="btn btn-sm btn-outline-success"
          @click="$router.push({ name: 'sales-validated', params: { reference: sale.reference } })"
        >
          <i class="bi bi-check2-square"></i> Valider
        </button>
        <button
          v-if="sale.reference && $userRole == 'Admin'"
          type="button"
          class="btn btn-sm btn-outline-primary"
          @click="$router.push({ name: 'mobile-sales-edit', params: { reference: sale.reference } })"
        >
          <i class="bi bi-pencil-square"></i> Modifier
        </button>
        <button
          v-if="sale.reference && $userRole == 'Admin'"
          type="button"
          class="btn btn-sm btn-outline-danger"
          @click="remove(sale.reference)"
        >
          <i class="bi bi-archive-fill"></i>
        </button>
        <button
          v-if="sale.reference && ($userRole == 'Admin' || $userRole == 'Importation' || $userRole == 'Comptable')"
          type="button"
          class="btn btn-sm btn-outline-primary"
          @click="createInvoice(sale.reference)"
        >
          <i class="bi bi-file-earmark-text"></i> Facture
        </button>
        <button v-if="sale.reference" type="button" class="btn btn-sm btn-outline-secondary" @click="print(sale.reference)">
          <i class="bi bi-printer"></i> BL
        </button>
        <button
          v-if="sale.reference"
          type="button"
          class="btn btn-sm btn-outline-secondary"
          @click="printQuotation(sale.reference)"
        >
          <i class="bi bi-printer"></i> BL (Qté)
        </button>
        <button type="button" class="btn btn-sm btn-outline-secondary" @click="refresh">
          <i class="bi bi-arrow-clockwise"></i>
        </button>
      </div>

      <section class="sal-detail-card">
        <h3 class="sal-detail-h">Résumé</h3>
        <dl class="sal-kv">
          <div><dt>Client</dt><dd>{{ sale.customerFullName || "—" }}</dd></div>
          <div><dt>Utilisateur</dt><dd>{{ sale.userName || "—" }}</dd></div>
          <div><dt>Total TTC</dt><dd class="text-primary">{{ sale.totalPriceTTC }} DH</dd></div>
          <div><dt>Qté totale</dt><dd>{{ sale.totalQuantity }}</dd></div>
          <div><dt>Payé</dt><dd class="text-success">{{ sale.amountPaid }} DH</dd></div>
          <div><dt>Impayé</dt><dd class="text-danger">{{ sale.amountUnpaid }} DH</dd></div>
          <div><dt>Validée</dt><dd :class="sale.validated ? 'text-success' : 'text-warning'">{{ sale.validated ? "Oui" : "Non" }}</dd></div>
          <div v-if="sale.remark"><dt>Remarque</dt><dd>{{ sale.remark }}</dd></div>
        </dl>
      </section>

      <section class="sal-detail-card">
        <h3 class="sal-detail-h">Articles</h3>
        <p v-if="!lineItems.length" class="text-muted mb-0">Aucune ligne.</p>
        <article
          v-for="(item, idx) in lineItems"
          :key="item.item_reference || idx"
          class="sal-line-card"
        >
          <div class="fw-semibold text-uppercase">{{ item.itemFullName || item.item_reference }}</div>
          <dl class="sal-kv sal-kv--compact mt-1 mb-0">
            <div><dt>Réf.</dt><dd>{{ item.item_reference }}</dd></div>
            <div><dt>Qté</dt><dd>{{ item.quantity }} {{ item.itemMeasuring || "" }}</dd></div>
            <div><dt>P.U. TTC</dt><dd>{{ item.priceTTC | toFixedWithSpace }}</dd></div>
            <div><dt>Total TTC</dt><dd>{{ item.totalPriceTTC | toFixedWithSpace }}</dd></div>
            <div v-if="item.stockFullName"><dt>Stock</dt><dd>{{ item.stockFullName }}</dd></div>
            <div v-if="item.remark"><dt>Rem.</dt><dd>{{ item.remark }}</dd></div>
          </dl>
        </article>
      </section>

      <section v-if="paymentRows.length" class="sal-detail-card">
        <h3 class="sal-detail-h">Paiements</h3>
        <article
          v-for="(payment, idx) in paymentRows"
          :key="payment.reference || idx"
          class="sal-pay-card"
        >
          <div class="d-flex justify-content-between align-items-start gap-2">
            <div>
              <div class="fw-semibold">{{ payment.reference }}</div>
              <div class="text-muted small">{{ payment.date }} · {{ payment.paymentMethod }}</div>
            </div>
            <button
              type="button"
              class="btn btn-sm btn-outline-primary"
              @click="goPayment(payment.reference)"
            >
              <i class="bi bi-info-square"></i>
            </button>
          </div>
          <dl class="sal-kv sal-kv--compact mt-2 mb-0">
            <div><dt>Payé</dt><dd class="text-success">{{ payment.amount | toFixedWithSpace }}</dd></div>
            <div><dt>À régler</dt><dd class="text-danger">{{ payment.remainingAmount | toFixedWithSpace }}</dd></div>
            <div v-if="payment.user && payment.user.name"><dt>Par</dt><dd>{{ payment.user.name }}</dd></div>
            <div v-if="payment.remark"><dt>Remarque</dt><dd>{{ payment.remark }}</dd></div>
          </dl>
        </article>
      </section>

      <section v-if="checkRows.length" class="sal-detail-card">
        <h3 class="sal-detail-h">Chèques</h3>
        <article
          v-for="(check, idx) in checkRows"
          :key="check.reference || idx"
          class="sal-check-card"
        >
          <div class="fw-semibold">{{ check.reference }}</div>
          <dl class="sal-kv sal-kv--compact mt-1 mb-0">
            <div><dt>Montant</dt><dd>{{ check.amount }}</dd></div>
            <div><dt>Banque</dt><dd>{{ check.bank }}</dd></div>
            <div><dt>Date</dt><dd>{{ check.date }}</dd></div>
            <div><dt>Échéance</dt><dd>{{ check.deadline }}</dd></div>
            <div><dt>Nº</dt><dd>{{ check.checkNumber }}</dd></div>
            <div>
              <dt>État</dt>
              <dd :class="check.validated === 'Validé' ? 'text-success' : 'text-danger'">{{ check.validated }}</dd>
            </div>
          </dl>
        </article>
      </section>
    </div>
    <p v-else class="text-muted text-center py-4">Chargement…</p>
  </mobile-responsive-shell>
</template>

<script>
import { mapGetters } from "vuex";
import MobileResponsiveShell from "../components/MobileResponsiveShell.vue";

export default {
  name: "MobileSalesDetails",
  components: { MobileResponsiveShell },
  computed: {
    ...mapGetters("sale", { sale: "getSale" }),
    title() {
      const ref = this.$route.params.reference;
      return ref ? "BL " + ref : "Détail vente";
    },
    lineItems() {
      const items = this.sale && this.sale.sale_items;
      return Array.isArray(items) ? items : [];
    },
    paymentRows() {
      const p = this.sale && this.sale.payments;
      return Array.isArray(p) ? p : [];
    },
    checkRows() {
      const c = this.sale && this.sale.checks;
      return Array.isArray(c) ? c : [];
    },
  },
  mounted() {
    this.$store.dispatch("sale/show", this.$route.params.reference);
  },
  methods: {
    async refresh() {
      await this.$store.dispatch("sale/show", this.$route.params.reference);
    },
    async recalculationPrices(reference) {
      await this.$store.dispatch("sale/recalculationPrices", reference);
      await this.refresh();
    },
    async addManutention(reference) {
      await this.$store.dispatch("sale/addManutention", reference);
      await this.refresh();
    },
    async print(reference) {
      await this.$store.dispatch("sale/print", reference);
    },
    async printQuotation(reference) {
      await this.$store.dispatch("sale/printQuotation", reference);
    },
    async remove(reference) {
      await this.$store.dispatch("sale/destroy", reference);
    },
    async createInvoice(reference) {
      await this.$store.dispatch("invoice/store", { sale_reference: reference });
    },
    goPayment(reference) {
      this.$router.push({
        name: "mobile-payments-customers-details",
        params: { reference },
      });
    },
  },
  filters: {
    toFixedWithSpace: (value) => {
      if (value === undefined || value === null || value === "") return "—";
      return parseFloat(value)
        .toFixed(2)
        .replace(/(\d)(?=(\d{3})+(?!\d))/g, "$1 ");
    },
  },
};
</script>

<style scoped>
.sal-detail-actions {
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
  margin-bottom: 1rem;
}
.sal-detail-card {
  background: #fff;
  border-radius: 12px;
  padding: 0.875rem 1rem;
  margin-bottom: 0.75rem;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.06);
  border: 1px solid rgba(0, 0, 0, 0.06);
}
.sal-detail-h {
  font-size: 1rem;
  font-weight: 700;
  margin: 0 0 0.5rem;
  color: #495057;
}
.sal-kv {
  margin: 0;
  display: grid;
  gap: 0.35rem;
}
.sal-kv > div {
  display: flex;
  justify-content: space-between;
  gap: 0.75rem;
  font-size: 1.05rem;
}
.sal-kv dt {
  margin: 0;
  color: #6c757d;
  font-weight: 500;
}
.sal-kv dd {
  margin: 0;
  text-align: right;
  word-break: break-word;
}
.sal-kv--compact > div {
  font-size: 1rem;
}
.sal-line-card,
.sal-pay-card,
.sal-check-card {
  padding: 0.65rem 0;
  border-bottom: 1px solid #eee;
}
.sal-line-card:last-child,
.sal-pay-card:last-child,
.sal-check-card:last-child {
  border-bottom: none;
}
</style>
